import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, History } from "lucide-react";
import { useTimezone } from "@/contexts/TimezoneContext";
import MeetingRoomCard from "./MeetingRoomCard";
import MeetingCard from "./MeetingCard";
import MeetingForm from "./MeetingForm";
import { notifyMeetingsChanged } from "./useMeetings";

function PastMeetings({ cardProps }) {
  const [past, setPast] = useState(null);
  const load = () => api.get("/meetings?scope=past").then(({ data }) => setPast(data.meetings)).catch(() => setPast([]));
  if (past === null) {
    return <button onClick={load} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="meetings-show-past"><History size={12} /> Show past meetings</button>;
  }
  return (
    <div className="space-y-3" data-testid="meetings-past-list">
      <div className="label-eyebrow">▮ PAST MEETINGS</div>
      {past.length === 0 ? <div className="text-xs text-[var(--ff-text-dim)]">No past meetings yet.</div>
        : past.map((m) => <MeetingCard key={m.id} m={m} {...cardProps} />)}
    </div>
  );
}

// Staff meetings: room card, schedule button (founder/executive), upcoming + past meetings.
export default function MeetingsPanel({ data, now, rsvp }) {
  const { timezone } = useTimezone();
  const [form, setForm] = useState(null); // null | "new" | meeting

  if (!data) return <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading meetings…</div>;
  if (data.forbidden) return <div className="text-[var(--ff-text-dim)] text-sm" data-testid="meetings-forbidden">Meetings are only for hosts and executive managers.</div>;

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); notifyMeetingsChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong"); }
  };
  const cardProps = {
    now, tz: timezone, canRsvp: data.can_rsvp, canManage: data.can_manage, canSeeResponses: data.can_set_room, onRsvp: rsvp,
    onEdit: (m) => setForm(m),
    onCancel: (m) => window.confirm(`Cancel "${m.title}"?${m.notify ? " Everyone invited gets a DM." : ""}`) && act(() => api.post(`/admin/meetings/${m.id}/cancel`), "Meeting canceled"),
    onDelete: (m) => window.confirm(`Delete "${m.title}" from the list? No DMs are sent.`) && act(() => api.delete(`/admin/meetings/${m.id}`), "Meeting deleted"),
  };

  return (
    <div className="space-y-6" data-testid="meetings-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ STAFF MEETINGS</div>
          <p className="text-sm text-[var(--ff-text-dim)] max-w-2xl">
            Every host and executive manager is invited. Let us know if you can make it — the meeting happens in the Discord voice room below.
          </p>
        </div>
        {data.can_manage && (
          <button onClick={() => setForm("new")} className="btn-primary text-xs" data-testid="meetings-schedule-btn"><Plus size={12} /> Schedule meeting</button>
        )}
      </div>

      <MeetingRoomCard room={data.room} canSetRoom={data.can_set_room} />

      <div className="space-y-3" data-testid="meetings-upcoming-list">
        <div className="label-eyebrow">▮ UPCOMING · {data.meetings.length}</div>
        {data.meetings.length === 0 ? (
          <div className="surface-card p-8 text-center text-sm text-[var(--ff-text-dim)]" data-testid="meetings-empty">No meetings scheduled right now.</div>
        ) : data.meetings.map((m) => <MeetingCard key={m.id} m={m} {...cardProps} />)}
      </div>

      <PastMeetings cardProps={cardProps} />

      {form && (
        <MeetingForm editing={form === "new" ? null : form} defaultRoom={data.room} onClose={() => setForm(null)}
          onSaved={() => { setForm(null); notifyMeetingsChanged(); }} />
      )}
    </div>
  );
}
